import { AmplitudeProductEvent } from '@cfa/pathway-fe-shared';

/**
 * # `resource_not_found` — Missing Plan Resource Tracking
 *
 * ## Purpose
 *
 * Passive Amplitude monitoring when team members open a plan and a document
 * step has a null resource or reference ID (PATHWAY-3685).
 *
 * ## Event
 *
 * **`resource_not_found`** — fires once per unique step error per browser
 * session (module-level Set dedupe).
 *
 * | Property          | Type   | Description                                      |
 * | ----------------- | ------ | ------------------------------------------------ |
 * | `plan_id`         | string | Plan the step belongs to                         |
 * | `step_id`         | string | Step within the plan                             |
 * | `resource_id`     | string \| null | Resource ID on the step, if any          |
 * | `resource_name`   | string \| null | Localized resource reference name        |
 * | `step_name`       | string | Localized step name at time of detection         |
 * | `plan_type`       | string | Plan type (e.g. STORE, ANCHOR)                   |
 * | `location_number` | string | Team member's primary location number            |
 * | `missing_fields`  | string[] | Null IDs: `resource_id`, `reference`, or both |
 * | `xp_step_available` | boolean | XP API `ContentStep.available` — drives plan-list warning |
 *
 * Properties captured at the Identify level (on every event automatically):
 * `is_webview`, `user_type`, `permission_level`, `user_locations`
 *
 * ## Flow
 *
 * ```
 * AssignedPlanView / DocumentProxy (plan data resolves)
 *   → useTrackPlanResourceErrors()
 *     → classifyPlanStepResourceError(step)
 *       → trackResourceNotFound()
 *         → track(AmplitudeProductEvent.RESOURCE_NOT_FOUND, { ... })
 * ```
 *
 * ## Analysis
 *
 * In Amplitude, filter `UserAction` where `action = resource_not_found`.
 * Group by `resource_id`, `plan_id`, `plan_type`, `location_number`,
 * `missing_fields`, and `xp_step_available` to trend cleanup progress.
 */
import type { PlanStepResourceMissingField } from './classifyPlanStepResourceError';

import { Track } from '@/amplitude/useAmplitude';

const trackedKeys = new Set<string>();

export interface ResourceNotFoundParams {
  plan_id: string;
  step_id: string;
  resource_id: string | null;
  resource_name: string | null;
  step_name: string | null;
  plan_type: string;
  location_number: string;
  missing_fields: PlanStepResourceMissingField[];
  xp_step_available: boolean;
}

export const trackResourceNotFound = (
  track: Track | null,
  params: ResourceNotFoundParams,
) => {
  const dedupeKey = `${params.plan_id}:${params.step_id}`;
  if (!track || trackedKeys.has(dedupeKey)) {
    return;
  }

  const sent = track(AmplitudeProductEvent.RESOURCE_NOT_FOUND, { ...params });
  if (!sent) {
    return;
  }

  trackedKeys.add(dedupeKey);
};
